<script setup>
import { ref, computed, onMounted, watchEffect } from 'vue';
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import store from '../../state/store';
import moment from 'moment'
import { cloneDeep } from 'lodash';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { required, helpers, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import Swal from 'sweetalert2';

const title = ref('Update Delivery Info');

const items = ref([
  {
    text: "Home",
  },
  {
    text: "Tracking",
  },
  {
    text: "Update Delivery Info",
    active: true,
  },
]);

const submitted = ref(false);
const submittedUpdate = ref(false);
const notification = computed(() => store.state.notification);
const loading = computed(() => store.state.globals.loading);
const trackingStatuses = computed(() => store.state.tracking.dropdownTrackingStatuses);
const trackingStatusReasons = computed(() => store.state.tracking.dropdownTrackingStatusReasons);

const inputTracking = ref(null);

const humanfriendlyConfig = ref({
  altInput: true,
  altFormat: "F j, Y",
  dateFormat: "Y/m/d"
});

const deliveryInfo = ref({
  receiver: '',
  date_delivered_or_returned: moment().format('YYYY/MM/DD'),
  remarks: '',
  status: 'DELIVERED',
  reason_id: '',
  tracking_number: ''
});

const search = ref({
  tracking_number: ''
})

const rules = computed(() => ({
   receiver: {
     requiredIfFunction: helpers.withMessage("Name of Receiver is required", requiredIf(() => deliveryInfo.value.status === 'DELIVERED'))
   },
   status: {
     required: helpers.withMessage("Tracking Status is required", required),
   },
   reason_id: {
     requiredIfFunction: helpers.withMessage("Reason is required", requiredIf(() => deliveryInfo.value.status === 'RETURNED'))
   }
}))

const v$ = useVuelidate(rules, deliveryInfo);

const results = computed(() => store.state.tracking.trackingDeliveryInfo ? store.state.tracking.trackingDeliveryInfo : null);

const cloneSearch = ref(null);

const scanTrackingBarcode = async () => {
  try {
    if (search.value.tracking_number !== '') {
      cloneSearch.value = cloneDeep(search.value);
    }
    
    submitted.value = true;
    await store.dispatch('tracking/getAllTrackingEntriesByDeliveryInfo', cloneSearch.value)

    store.dispatch('notification/clear', null, { root: true });
  } catch (err) {
    console.log(err);
  }
}

const scanTrackingBarcodeWithTimeout = () => {
  setTimeout(scanTrackingBarcode, 0);
}

const getTrackingStatusType = (tracking_status_id) => {
    if (!tracking_status_id) return 'N/A';

    if (trackingStatuses.value.length > 0) {
      const trackingStatus = trackingStatuses.value.find((el) => el.id === tracking_status_id);
      return trackingStatus.system_name;
    }
    return 'N/A';
}

const updateDeliveryInfo = () => {
  if (!(['DELIVERED', 'RETURNED']).includes(deliveryInfo.value.status)) {
    Swal.fire(
        "Warning", 
        `You are not allowed to update this tracking number. Please go to Delivered/Returned (RTS) page.`, 
        "error"
    );
    return;
  } 

  Swal.fire({
      title: "Do you want to update?",
      text: `This will update the tracking status information?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#34c38f",
      cancelButtonColor: "#f46a6a",
      confirmButtonText: "Yes",
  }).then(async (result) => {
      if (result.value) {
          submittedUpdate.value = true;

          v$.value.$touch();
          if (v$.value.$invalid) return;  
  
          deliveryInfo.value.tracking_number = cloneSearch.value ? cloneSearch.value.tracking_number : search.value.tracking_number;

          await store.dispatch('tracking/updateDeliveryInfo', deliveryInfo.value);

          deliveryInfo.value.receiver = '';
          deliveryInfo.value.date_delivered_or_returned = moment().format('YYYY/MM/DD');
          deliveryInfo.value.remarks = '';
          deliveryInfo.value.reason_id = '';
          v$.value.$reset();
          submittedUpdate.value = false;

          scanTrackingBarcode();
          scanTrackingBarcode();

          search.value.tracking_number = '';
      }
  })
}

onMounted(() => {
  store.dispatch('tracking/getDropdownTrackingStatuses');
  store.dispatch('tracking/getDropdownTrackingStatusReasons');

  inputTracking.value.focus();
});

watchEffect(() => {
  if (results.value) {
    if (results.value.tracking_status_type.name === 'RETURNED') {
      return deliveryInfo.value.status = 'RETURNED'
    } else if (results.value.tracking_status_type.name === 'DELIVERED') {
      return deliveryInfo.value.status = 'DELIVERED'
    } else {
      return deliveryInfo.value.status = results.value.tracking_status_type.name;
    }
  }
})

</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-md-12 col-lg-4">
        <b-overlay :show="loading">
          <div class="card">
            <div class="card-header">
              <h4>Update Delivery Info</h4>
              <div class="text-muted">Search barcode and update the delivery information</div>
            </div>
            <div class="card-body">
                <form @submit.prevent="scanTrackingBarcode()">
                  <div class="mb-3">
                    <label for="barcode" class="form-label">Scan Tracking Barcode</label>
                    <input
                      type="text" autofocus autocomplete="off" ref="inputTracking" @paste="scanTrackingBarcodeWithTimeout" @keydown.enter="scanTrackingBarcode" v-model="search.tracking_number" class="form-control" id="barcode" placeholder="Scan tracking barcode here...">
                  </div>
                  <!-- <div class="mb-3">
                    <button type="submit" :disabled="!search.tracking_number" class="btn btn-primary">
                      <i class="bx bx-search mr-2 align-middle fs-4 text"></i> Search
                    </button>
                  </div> -->
                </form>
            </div>
          </div>
        </b-overlay>
      </div>
      <div class="col-md-12 col-lg-8">
        <b-overlay :show="loading">
          <div class="card">
            <div class="card-body">
              <div v-if="results && results.tracking_status_entries.length && submitted" class="table-card m-1">
                <div class="mb-4">
                    <h5>Tracking Number: {{ cloneSearch ? cloneSearch.tracking_number : 'N/A' }}</h5>
                    <h5>Courier: {{ results.tracking_courier_id ? results.tracking_courier.name : 'N/A' }}</h5>
                </div>

                <table class="table table-nowrap mb-4">
                    <thead class="table-light">
                        <tr>
                          <th scope="col">Entry No.</th>
                          <th scope="col">Status</th>
                          <th scope="col">Processed By</th>
                          <th scope="col">Date of Entry</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(result, index) in results.tracking_status_entries" 
                          :class="{ 'bg-success': result.id === results.tracking_status_entries[results.tracking_status_entries.length - 1].id && results.tracking_status_type 
                            && result.tracking_status_type.name === 'DELIVERED',
                            'bg-danger': result.id === results.tracking_status_entries[results.tracking_status_entries.length - 1].id && results.tracking_status_type 
                            && result.tracking_status_type.name === 'RETURNED' }" 
                          :key="result.id">
                            <td>
                              {{ index + 1 }}
                            </td>
                            <td>
                              {{ result.tracking_status_type_id ? getTrackingStatusType(result.tracking_status_type_id) : 'No status' }}
                            </td>
                            <td>
                              {{ result.user_creator ? result.user_creator.name : 'N/A' }}
                            </td>
                             <td>
                              {{ moment(result.created_at).format('YYYY-MM-DD hh:mm A') }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="mb-4 p-3" v-if="results.tracking_status_entry_info && results.tracking_status_entries.length && results.tracking_status_type.name === 'DELIVERED'">
                  <div class="row mb-2">
                    <div class="col-md-3"><strong>Received by: </strong></div>
                    <div class="col-md-9">{{ results.tracking_status_entry_info.receiver || 'N/A' }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-md-3"><strong>Date Delivered: </strong></div>
                    <div class="col-md-9">{{ results.tracking_status_entry_info.date_delivered_or_returned ? moment(results.tracking_status_entry_info.date_delivered_or_returned).format('YYYY-MM-DD') : 'N/A' }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-md-3"><strong>Remarks:</strong></div>
                    <div class="col-md-9">{{ results.tracking_status_entry_info.remarks || 'None' }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-md-3"><strong>Submitted by: </strong></div>
                    <div class="col-md-9">{{ results.tracking_status_entries ? results.tracking_status_entries[results.tracking_status_entries.length - 1].user_creator?.name : 'N/A' }}</div>
                  </div>
                </div>

                <div class="mb-4 p-3" v-if="results.tracking_status_entry_info && results.tracking_status_entries.length && results.tracking_status_type.name === 'RETURNED'">
                  <div class="row mb-2">
                    <div class="col-md-3"><strong>Reason: </strong></div>
                    <div class="col-md-9">{{ results.tracking_status_type_reason ? results.tracking_status_type_reason.public_name : 'N/A' }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-md-3"><strong>Date Returned: </strong></div>
                    <div class="col-md-9">{{ results.tracking_status_entry_info.date_delivered_or_returned ? moment(results.tracking_status_entry_info.date_delivered_or_returned).format('YYYY-MM-DD') : 'N/A' }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-md-3"><strong>Remarks:</strong></div>
                    <div class="col-md-9">{{ results.tracking_status_entry_info.remarks || 'None' }}</div>
                  </div>
                  <div class="row mb-2">
                    <div class="col-md-3"><strong>Submitted by: </strong></div>
                    <div class="col-md-9">{{ results.tracking_status_entries[results.tracking_status_entries.length - 1].user_creator?.name || 'N/A' }}</div>
                  </div>
                </div>

               
                  <form v-if="(['DELIVERED', 'RETURNED']).includes(deliveryInfo.status)" @submit.prevent="updateDeliveryInfo">
                   <div class="row">
                    <div v-if="deliveryInfo.status === 'DELIVERED'" class="col-md-12 col-lg-4">
                      <label for="receiver" class="form-label">Name of Receiver</label>
                      <input 
                         :disabled="deliveryInfo.status === 'RETURNED'"
                         :class="{ 'is-invalid': submittedUpdate && v$.receiver.$error }"
                        type="text" v-model="deliveryInfo.receiver" class="form-control" id="receiver" placeholder="Enter name of receiver...">
                      <div
                        v-for="(item, index) in v$.receiver.$errors"
                        :key="index"
                        class="invalid-feedback"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div>
                    <div v-if="deliveryInfo.status === 'RETURNED'" class="col-md-12 col-lg-4">
                      <label for="reason" class="form-label">Reason</label>
                      <select
                        :disabled="deliveryInfo.status === 'DELIVERED'"
                        :class="{ 'is-invalid': submittedUpdate && v$.reason_id.$error }"
                        class="form-select" v-model="deliveryInfo.reason_id">
                      <option value="">Select Reason</option>
                        <option :value="reason.id" v-for="reason in trackingStatusReasons" :key="reason.id">{{ reason.system_name }}</option>
                      </select>
                      <div
                        v-for="(item, index) in v$.reason_id.$errors"
                        :key="index"
                        class="invalid-feedback"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div>
                    <div class="col-md-12 col-lg-4">
                      <label for="date_received" class="form-label">{{ deliveryInfo.status === 'DELIVERED' ? 'Date Received' : 'Date Returned' }}</label>
                       <flatPickr
                            id="date"
                            v-model="deliveryInfo.date_delivered_or_returned"
                            :config="humanfriendlyConfig"
                            class="form-control flatpickr-input"
                        ></flatPickr>
                    </div>
                    <div class="col-md-12 col-lg-4">
                      <label for="remarks" class="form-label">Remarks</label>
                      <textarea v-model="deliveryInfo.remarks" class="form-control" id="remarks" placeholder="Enter remarks..."></textarea>
                    </div>
                     <!-- <div class="col-md-12 col-lg-4">
                      <label for="reason" class="form-label required">Status</label>
                      <select
                        :class="{ 'is-invalid': submittedUpdate && v$.status.$error }"
                        class="form-select" v-model="deliveryInfo.status">
                        <option value="DELIVERED">DELIVERED</option>
                        <option value="RETURNED">RETURNED</option>
                      </select>
                      <div
                        v-for="(item, index) in v$.status.$errors"
                        :key="index"
                        class="invalid-feedback"
                      >
                        <span v-if="item.$message">{{ item.$message }}</span>
                      </div>
                    </div> -->
                    <div class="col-md-12 col-lg-12 mt-3">
                      <button type="submit" class="btn btn-success">
                        <i class="bx bx-save fs-4 mr-2 text align-middle"></i> Submit
                      </button>
                    </div>
                    </div>
                  </form>
              </div>
              <div v-else-if="results && results.tracking_status_entries.length === 0 && !notification.message && submitted">
                <div class="text-center">
                  <div class="row justify-content-center">
                    <div class="col-lg-9">
                      <h4 class="mt-4 fw-semibold">Update Delivery Info</h4>
                      <p class="text-danger mt-3 fs-16 text"> 
                        No tracking entries found!
                      </p>
                    </div>
                  </div>
                  <div class="row justify-content-center mt-5 mb-2">
                    <div class="col-sm-7 col-8">
                      <img src="@/assets/images/file.png" alt="" class="img-fluid">
                    </div>
                  </div>
                </div>
              </div>
               <div v-else-if="notification && notification.message && submitted">
                <div class="text-center">
                  <div class="row justify-content-center">
                    <div class="col-lg-9">
                     <h4 class="mt-4 fw-semibold">Update Delivery Info</h4>
                      <p class="text-danger mt-3 fs-16 text"> 
                        {{ notification.message }}
                      </p>
                    </div>
                  </div>
                  <div class="row justify-content-center mt-5 mb-2">
                    <div class="col-sm-7 col-8">
                      <img src="@/assets/images/error500.png" alt="" class="img-fluid">
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="text-center">
                  <div class="row justify-content-center">
                    <div class="col-lg-9">
                      <h4 class="mt-4 fw-semibold">Update Delivery Info</h4>
                      <p class="text-muted mt-3 fs-16 text"> 
                        Scanned tracking barcode results will show up their results here
                      </p>
                    </div>
                  </div>
                  <div class="row justify-content-center mt-5 mb-2">
                    <div class="col-sm-7 col-8">
                      <img src="@/assets/images/file.png" alt="" class="img-fluid">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </div>
    </div>
  </Layout>
</template>